import React, { useState } from "react";
import { toast } from "sonner";// TODO: Implement custom API client
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import {
  ArrowUpFromLine,
  Search,
  Calendar,
  ChevronLeft,
  ChevronRight,
  X,
  History,
  Plus,
} from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import { Calendar as CalendarComponent } from "@/shared/components/ui/calendar";
import { format } from "date-fns";
import MerchantLayout from "@/shared/components/merchant/MerchantLayout";
import TransactionTable from "@/shared/components/merchant/TransactionTable";
import TransactionDetailPanel from "@/shared/components/merchant/TransactionDetailPanel";
import WithdrawalForm from "@/shared/components/withdrawals/WithdrawalForm";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/components/ui/tabs";

export default function MerchantWithdrawals() {
  const { t } = useLanguage();
  const [submitting, setSubmitting] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [page, setPage] = useState(0);
  const pageSize = 10;

  // TODO: Implement custom API calls
  const { data: transactions = [], isLoading } = useQuery({
    queryKey: ["merchant-withdrawals"],
    queryFn: async () => [], // TODO: Implement Transaction.list (withdrawals) API call
  });

  const filteredTransactions = React.useMemo(() => {
    return transactions.filter((tx) => {
      if (statusFilter !== "all" && tx.status !== statusFilter) return false;
      if (dateRange.from && new Date(tx.created_date) < dateRange.from) return false;
      if (dateRange.to && new Date(tx.created_date) > dateRange.to) return false;
      return true;
    });
  }, [transactions, statusFilter, dateRange]);

  const paginatedTransactions = filteredTransactions.slice(
    page * pageSize,
    (page + 1) * pageSize
  );
  const totalPages = Math.ceil(filteredTransactions.length / pageSize);

  const handleWithdrawalSubmit = async (data) => {
    setSubmitting(true);
    // Simulate API call
    await new Promise((r) => setTimeout(r, 1500));
    setSubmitting(false);
    toast.success(`${t("withdrawalRequestSubmittedSuccessfully")} Type: ${data.type}`);
  };

  const clearFilters = () => {
    setStatusFilter("all");
    setDateRange({ from: null, to: null });
    setPage(0);
  };

  const hasFilters = statusFilter !== "all" || dateRange.from;

  // Stats
  const stats = React.useMemo(() => {
    const success = transactions.filter((t) => t.status === "success");
    const pending = transactions.filter((t) => t.status === "pending" || t.status === "processing");
    return {
      total: success.reduce((sum, t) => sum + (t.amount || 0), 0),
      pendingCount: pending.length,
      pendingAmount: pending.reduce((sum, t) => sum + (t.amount || 0), 0),
    };
  }, [transactions]);

  return (
    <MerchantLayout currentPage="MerchantWithdrawals" merchantName="Royal Gaming" merchantCode="MRC001">
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
            <ArrowUpFromLine className="h-5 w-5 text-emerald-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">{t("withdrawals")}</h1>
            <p className="text-slate-500">{t("requestPayoutsViaBankUsdt")}</p>
          </div>
        </div>

        <Tabs defaultValue="new" className="space-y-6">
          <TabsList className="bg-white border p-1">
            <TabsTrigger value="new" className="gap-2 px-4">
              <Plus className="h-4 w-4" />
              {t("newWithdrawal")}
            </TabsTrigger>
            <TabsTrigger value="history" className="gap-2 px-4">
              <History className="h-4 w-4" />
              {t("history")}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="new">
            <WithdrawalForm
              onSubmit={handleWithdrawalSubmit}
              submitting={submitting}
              stats={stats}
              accentColor="emerald"
            />
          </TabsContent>

          <TabsContent value="history">
            {/* Filters */}
            <div className="bg-white rounded-2xl p-4 border border-slate-100 mb-4">
              <div className="flex flex-wrap gap-3">
                <Select
                  value={statusFilter}
                  onValueChange={(v) => {
                    setStatusFilter(v);
                    setPage(0);
                  }}
                >
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder={t("status")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("allStatus")}</SelectItem>
                    <SelectItem value="success">{t("success")}</SelectItem>
                    <SelectItem value="pending">{t("pending")}</SelectItem>
                    <SelectItem value="processing">{t("processing")}</SelectItem>
                    <SelectItem value="failed">{t("failed")}</SelectItem>
                  </SelectContent>
                </Select>

                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="gap-2">
                      <Calendar className="h-4 w-4" />
                      {dateRange.from ? (
                        dateRange.to ? (
                          `${format(dateRange.from, "MMM d")} - ${format(dateRange.to, "MMM d")}`
                        ) : (
                          format(dateRange.from, "MMM d")
                        )
                      ) : (
                        t("dateRange")
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <CalendarComponent
                      mode="range"
                      selected={dateRange}
                      onSelect={(range) => {
                        setDateRange(range || { from: null, to: null });
                        setPage(0);
                      }}
                      numberOfMonths={2}
                    />
                  </PopoverContent>
                </Popover>

                {hasFilters && (
                  <Button variant="ghost" onClick={clearFilters} className="gap-1 text-slate-500">
                    <X className="h-4 w-4" />
                    {t("clear")}
                  </Button>
                )}
              </div>
            </div>

            {/* Table */}
            <TransactionTable
              transactions={paginatedTransactions}
              isLoading={isLoading}
              onRowClick={setSelectedTransaction}
            />

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <p className="text-sm text-slate-500">
                  {t("showing")} {page * pageSize + 1} {t("to")}{" "}
                  {Math.min((page + 1) * pageSize, filteredTransactions.length)} {t("of")}{" "}
                  {filteredTransactions.length}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(page - 1)}
                    disabled={page === 0}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm">{t("page")} {page + 1} {t("of")} {totalPages}</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(page + 1)}
                    disabled={page >= totalPages - 1}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      <TransactionDetailPanel
        transaction={selectedTransaction}
        open={!!selectedTransaction}
        onClose={() => setSelectedTransaction(null)}
      />
    </MerchantLayout>
  );
}